import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Star, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';

const TOPICS = [
  'Produktvorstellung / Demo anfragen',
  'Frage zum Produkt',
  'Technischer Support',
  'Partnerschaft / Kooperation',
  'Presse & Medien',
  'Sonstiges',
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

export default function ContactPage() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    topic: '',
    message: '',
    privacy_accepted: false,
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const next = () => { setDirection(1); setStep((s) => s + 1); };
  const back = () => { setDirection(-1); setStep((s) => s - 1); };

  const canNext1 = form.name.trim() && form.email.trim();
  const canNext2 = form.topic;
  const canSubmit = form.message.trim().length >= 10 && form.privacy_accepted;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      // fetch wirft bei 4xx/5xx nicht — ohne diese Prüfung gingen Anfragen still verloren
      if (!res.ok) {
        throw new Error(res.status === 429 ? 'Zu viele Anfragen. Bitte später erneut versuchen.' : 'send failed');
      }
      setDone(true);
    } catch (err) {
      toast.error(err?.message?.startsWith('Zu viele') ? err.message : 'Fehler beim Senden. Bitte versuchen Sie es erneut oder schreiben Sie an af@dwayne-advertising.de.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Header */}
      <header className="bg-background border-b border-border px-5 py-4">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Immoverita" className="h-28 w-auto" />
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold mb-2">Vielen Dank!</h1>
              <p className="text-muted-foreground mb-6">Ihre Nachricht ist bei uns eingegangen. Wir melden uns in Kürze bei Ihnen.</p>
              <Link to="/"><Button variant="outline">Zurück zur Startseite</Button></Link>
            </motion.div>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between mb-1">
                  <CardTitle className="text-xl">Kontakt aufnehmen</CardTitle>
                  <span className="text-sm text-muted-foreground">{step} / 3</span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3].map((s) => (
                    <div
                      key={s}
                      className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-muted'}`}
                    />
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <AnimatePresence mode="wait" custom={direction}>
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input id="name" className="mt-1" placeholder="Max Mustermann" value={form.name} onChange={set('name')} />
                      </div>
                      <div>
                        <Label htmlFor="company">Unternehmen</Label>
                        <Input id="company" className="mt-1" placeholder="Muster Hausverwaltung GmbH" value={form.company} onChange={set('company')} />
                      </div>
                      <div>
                        <Label htmlFor="email">E-Mail-Adresse *</Label>
                        <Input id="email" type="email" className="mt-1" placeholder="max@hausverwaltung.de" value={form.email} onChange={set('email')} />
                      </div>
                      <Button className="w-full mt-2" onClick={next} disabled={!canNext1}>
                        Weiter <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-3"
                    >
                      <p className="text-sm text-muted-foreground mb-4">Worum geht es?</p>
                      {TOPICS.map((t) => (
                        <button
                          key={t}
                          onClick={() => setForm((f) => ({ ...f, topic: t }))}
                          className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                            form.topic === t
                              ? 'border-primary bg-primary/5 font-medium'
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                      <div className="flex gap-2 mt-4">
                        <Button variant="ghost" onClick={back} className="flex-1">
                          <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                        </Button>
                        <Button className="flex-1" onClick={next} disabled={!canNext2}>
                          Weiter <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2 }}
                      className="space-y-4"
                    >
                      <div>
                        <Label htmlFor="message">Ihre Nachricht *</Label>
                        <Textarea
                          id="message"
                          className="mt-1"
                          rows={5}
                          placeholder="Beschreiben Sie kurz Ihr Anliegen…"
                          value={form.message}
                          onChange={set('message')}
                        />
                      </div>
                      <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.privacy_accepted}
                          onChange={(e) => setForm((f) => ({ ...f, privacy_accepted: e.target.checked }))}
                          className="mt-0.5 accent-primary"
                          data-testid="contact-privacy-checkbox"
                        />
                        <span>
                          Ich bin einverstanden, dass meine Angaben zur Beantwortung der Anfrage gespeichert werden.
                          Details in der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline">Datenschutzerklärung</a>.
                        </span>
                      </label>
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={back} className="flex-1">
                          <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                        </Button>
                        <Button className="flex-1" onClick={handleSubmit} disabled={!canSubmit || submitting}>
                          {submitting ? 'Senden…' : 'Nachricht senden'}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <footer className="bg-background border-t border-border py-4 text-center">
        <p className="text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">Immoverita</Link> · ISQI GmbH
        </p>
      </footer>
    </div>
  );
}
